import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";

import CartItem from "../cart-item/cart-item.vue";
import QuantitySelector from "../quantity-selector/quantity-selector.vue";
import "../image/image.vue";
import store from "../../js/lib/store";
import { createApp, defineComponent, type ComponentOptions } from "vue";

export function buildCartGridDefinition(): ComponentOptions {
  return {
    name: "cart-grid",
    components: {
      CartItem,
      QuantitySelector,
    },
    data() {
      return {
        baseUrl: (window as any).locale_aware_base_url?.replace("/", "") || "",
        store,
        ymalAll: [] as any[],
        ymalAddingId: 0 as number | string,
        ymalSwiper: null as any,
        discountTiers: [] as any[],
        cartBodyResizeHandler: null as null | (() => void),
        cartBodyRaf: 0 as number,
      };
    },
    computed: {
      subtotal() {
        return store.subtotal;
      },
      originalSubtotal() {
        return store.originalSubtotal;
      },
      isDiscounted() {
        return store.isCartDiscounted;
      },
      cartDiscountPercent(): number {
        const cart = store.cart;
        if (!cart) {
          return 0;
        }

        // Prefer the discount application's own percentage value. Shopify
        // reports order-level automatic discounts here as e.g. value "20.0"
        // with value_type "percentage", which is exact regardless of
        // per-line cent rounding or subscription/selling-plan pricing.
        const applications = [
          ...(cart.cart_level_discount_applications || []),
          ...(cart.discount_applications || []),
        ];

        for (const application of applications) {
          if (
            application &&
            application.value_type === "percentage" &&
            application.value != null
          ) {
            const percent = parseFloat(application.value);
            if (!Number.isNaN(percent) && percent > 0) {
              return Math.round(percent);
            }
          }
        }

        // Fallback: derive the percentage from the cart totals.
        const original = cart.original_total_price;
        const discount = cart.total_discount;

        if (!original || !discount) {
          return 0;
        }

        return Math.round((discount / original) * 100);
      },
      activeDiscountTier(): any {
        const percent = this.cartDiscountPercent;
        if (!percent) {
          return null;
        }

        return (
          (this.discountTiers || []).find(
            (tier: any) => parseFloat(tier.amount) === percent
          ) || null
        );
      },
      activeDiscountLabel(): string {
        return this.activeDiscountTier
          ? `${this.activeDiscountTier.amount} off`
          : "";
      },
      loading() {
        return store.loading;
      },
      isBootstrapped() {
        return store.isBootstrapped;
      },
      isMobile() {
        return store.isMobile;
      },
      isEmpty() {
        if (!store.cart) {
          return true;
        }

        if (!store.cart.items) {
          return true;
        }

        return store.cart.items.length === 0;
      },
      cart() {
        return store.cart;
      },
      cartVariantIds(): Array<number | string> {
        const items = store.cart?.items || [];
        return items.map((item: any) => item.variant_id);
      },
      ymalProducts(): any[] {
        const ids = this.cartVariantIds;
        return (this.ymalAll || []).filter(
          (product: any) =>
            product &&
            product.available &&
            product.variantId &&
            !ids.includes(product.variantId)
        );
      },
      hasFreeProductGwp() {
        const items = store.cart?.items || [];
        return items.some((item: any) => {
          const raw = item?.properties?.["_free-product"];
          return raw === 1 || raw === "1" || raw === true;
        });
      },
      awayFromFree() {
        if (!store.cart || !store.cart.items) {
          return false;
        }

        const subtotal = store.cart.total_price;

        const hasSubscription = store.cart.items.find((item: any) => {
          if (
            item.selling_plan_allocation &&
            item.selling_plan_allocation.selling_plan.id
          ) {
            return true;
          }
        });

        if (hasSubscription) {
          return false;
        }

        const freeShipping = (window as any).FREE_SHIPPING;

        if (freeShipping) {
          const value = parseInt(freeShipping) / 1;

          let diff = 0;

          if (subtotal < value) {
            diff = (value - subtotal) / 100;

            const diffFormat = diff.toFixed(2);

            return diffFormat.replace(".00", "");
          }

          return diff;
        }

        return false;
      },
    },
    watch: {
      "store.isMiniCartOpen"(value: boolean) {
        if (value) {
          // Lock after layout so .cart__body has its max-height and is the
          // actual scroll target body-scroll-lock should allow on iOS.
          this.$nextTick(() => {
            this.setCartBodyMaxHeight();
            this.lockMiniCartBodyScroll();
          });
        } else {
          history.pushState("", document.title, window.location.pathname);
          clearAllBodyScrollLocks();
          this.$nextTick(() => this.setCartBodyMaxHeight());
        }
      },
      "store.lastAdd"(_value: number) {
        store.getCart();
      },
      ymalProducts() {
        this.$nextTick(() => {
          this.refreshYmalSwiper();
          this.setCartBodyMaxHeight();
        });
      },
      isEmpty(value: boolean) {
        if (!value) {
          this.$nextTick(() => {
            this.loadYmal();
            this.loadDiscountTiers();
            if (store.isMiniCartOpen) {
              this.setCartBodyMaxHeight();
              this.lockMiniCartBodyScroll();
            }
          });
        }

        this.$nextTick(() => this.setCartBodyMaxHeight());
      },
      cart() {
        this.$nextTick(() => this.setCartBodyMaxHeight());
      },
    },
    methods: {
      getCart() {
        return store.getCart();
      },
      bootstrap() {
        store.bootstrap();
      },
      toggleMiniCart() {
        store.toggleMiniCart();
      },
      loadYmal() {
        const root: any = this.$el;
        const wrapper =
          root && root.querySelector
            ? root.querySelector(".ymal-wrapper")
            : null;

        if (!wrapper) {
          return;
        }

        if (!this.ymalAll.length) {
          try {
            const raw = wrapper.dataset.ymalProducts;
            this.ymalAll = raw ? JSON.parse(raw) : [];
          } catch (error) {
            console.error("Failed to parse YMAL products", error);
            this.ymalAll = [];
          }
        }

        this.$nextTick(() => this.initYmalSwiper());
      },
      loadDiscountTiers() {
        const root: any = this.$el;
        const wrapper =
          root && root.querySelector
            ? root.querySelector(".tiered-discount-wrapper")
            : null;

        if (!wrapper) {
          return;
        }

        if (!this.discountTiers.length) {
          try {
            const raw = wrapper.dataset.discountTiers;
            this.discountTiers = raw ? JSON.parse(raw) : [];
          } catch (error) {
            console.error("Failed to parse discount tiers", error);
            this.discountTiers = [];
          }
        }
      },
      initYmalSwiper() {
        const SwiperCtor = (window as any).Swiper;

        if (typeof SwiperCtor === "undefined") {
          window.setTimeout(() => this.initYmalSwiper(), 50);
          return;
        }

        const root: any = this.$el;
        const el =
          root && root.querySelector
            ? root.querySelector(".ymal-swiper")
            : null;

        if (!el || el.swiper) {
          return;
        }

        this.ymalSwiper = new SwiperCtor(el, {
          spaceBetween: 12,
          watchOverflow: true,
          slidesOffsetBefore: 120,
          pagination: {
            el: el.querySelector(".swiper-pagination"),
            clickable: true,
          },
          breakpoints: {
            320: {
              slidesPerView: 1.2
            },
            481: {
              slidesPerView: 2.1
            }
          },
          navigation: {
            nextEl: el.querySelector(".swiper-button-next"),
            prevEl: el.querySelector(".swiper-button-prev"),
          },
        });
      },
      refreshYmalSwiper() {
        if (this.ymalSwiper) {
          this.ymalSwiper.update();
        } else {
          this.initYmalSwiper();
        }
      },
      async addYmal(product: any) {
        if (!product || !product.variantId || this.ymalAddingId) {
          return;
        }

        this.ymalAddingId = product.variantId;

        try {
          await store.addToCart(product.variantId, 1);
        } catch (error) {
          console.error("Failed to add YMAL product to cart", error);
        } finally {
          this.ymalAddingId = 0;
        }
      },
      getSmallViewportHeight(): number {
        if (typeof document === "undefined") {
          return 0;
        }

        const probe = document.createElement("div");
        probe.style.cssText =
          "position:fixed;top:0;left:0;width:0;height:100svh;visibility:hidden;pointer-events:none;";
        document.body.appendChild(probe);
        const height = probe.getBoundingClientRect().height;
        document.body.removeChild(probe);

        return height || window.innerHeight || 0;
      },
      getCartElHeight(selector: string): number {
        const root: any = this.$el;
        if (!root || !root.querySelector) {
          return 0;
        }

        const el = root.querySelector(selector);
        if (!el) {
          return 0;
        }

        const rect = el.getBoundingClientRect();
        // getBoundingClientRect() includes padding + border (border-box),
        // but not margins, so add the vertical margins explicitly.
        const height = rect ? rect.height : 0;
        const styles = window.getComputedStyle(el);
        const marginTop = parseFloat(styles.marginTop) || 0;
        const marginBottom = parseFloat(styles.marginBottom) || 0;

        return height + marginTop + marginBottom;
      },
      setCartBodyMaxHeight() {
        const root: any = this.$el;
        if (!root || !root.querySelector) {
          return;
        }

        const body = root.querySelector(".cart__body");
        if (!body) {
          return;
        }

        const viewportHeight = this.getSmallViewportHeight();
        const headerHeight = this.getCartElHeight(".cart-grid__header");
        // const promoHeight = this.getCartElHeight(".shipping__promo");
        const tieredHeight = this.getCartElHeight(".tiered-discount-wrapper");
        const footerHeight = this.getCartElHeight(".cart__footer");

        const maxHeight = Math.max(
          viewportHeight -
            headerHeight -
            // promoHeight -
            tieredHeight -
            footerHeight,
          0
        );

        body.style.maxHeight = `${maxHeight}px`;
      },
      scheduleCartBodyMaxHeight() {
        if (typeof window === "undefined") {
          return;
        }

        if (this.cartBodyRaf) {
          window.cancelAnimationFrame(this.cartBodyRaf);
        }

        this.cartBodyRaf = window.requestAnimationFrame(() =>
          this.setCartBodyMaxHeight()
        );
      },
      /**
       * body-scroll-lock must target the element that actually scrolls —
       * `.cart__body` — not `#mini-cart`. Passing a non-scrolling parent makes
       * iOS preventDefault every touchmove and the page behind scrolls instead.
       */
      lockMiniCartBodyScroll() {
        const root: any = this.$el;
        const cartBody =
          root && root.querySelector
            ? root.querySelector(".cart__body")
            : null;
        const miniCart = document.querySelector("#mini-cart");
        const target = (cartBody || miniCart) as HTMLElement | null;

        if (!target) {
          return;
        }

        clearAllBodyScrollLocks();
        disableBodyScroll(target, {
          // Keep YMAL horizontal swipes working without unlocking the page.
          allowTouchMove: (el: HTMLElement | Element) =>
            Boolean(
              el instanceof Element &&
                typeof el.closest === "function" &&
                el.closest(".ymal-swiper")
            ),
        });
      },
    },
    mounted() {
      store.bootstrap();
      this.loadYmal();
      this.loadDiscountTiers();

      this.cartBodyResizeHandler = () => this.scheduleCartBodyMaxHeight();
      window.addEventListener("resize", this.cartBodyResizeHandler);
      this.$nextTick(() => this.setCartBodyMaxHeight());
    },
    beforeUnmount() {
      if (this.cartBodyResizeHandler) {
        window.removeEventListener("resize", this.cartBodyResizeHandler);
        this.cartBodyResizeHandler = null;
      }

      if (this.cartBodyRaf) {
        window.cancelAnimationFrame(this.cartBodyRaf);
        this.cartBodyRaf = 0;
      }
    },
  };
}

export function mountCartGridFromEl(el: HTMLElement) {
  const template = el.innerHTML;
  el.innerHTML = "";
  return createApp(
    defineComponent({
      ...(buildCartGridDefinition() as any),
      template,
    })
  ).mount(el);
}

/** Mini-cart uses the same cart logic plus `app` alias used in legacy Liquid. */
export function mountMiniCartFromEl(el: HTMLElement) {
  const template = el.innerHTML;
  el.innerHTML = "";
  const def = buildCartGridDefinition();
  return createApp(
    defineComponent({
      ...(def as any),
      name: "mini-cart",
      template,
      computed: {
        ...(def.computed as Record<string, unknown>),
        app() {
          return store;
        },
      },
    } as any)
  ).mount(el);
}

export const CartGrid = defineComponent(buildCartGridDefinition() as any);

export default mountCartGridFromEl;
